import React from 'react';
import { IAssistantMessage } from '../../../types';
import { ChatMessage } from '../../message-box';
import { useCurrency } from '@/features/product-configurator/hooks/useCurrency';

interface PriceSurgeMessageBubbleProps {
    message: IAssistantMessage;
}

export const PriceSurgeMessageBubble: React.FC<PriceSurgeMessageBubbleProps> = ({ message }) => {
    const { surgeItems } = message;
    const { formatPrice } = useCurrency();

    if (!surgeItems || surgeItems.length === 0) {
        return null;
    }

    // Merge items with the same ruleId into one row with a combined price.
    const mergedItems = Object.values(
        surgeItems.reduce<Record<string, typeof surgeItems[0] & { surgeAmount: number; vssSurgeAmount: number }>>((acc:any, item) => {
            if (acc[item.ruleId]) {
                acc[item.ruleId] = {
                    ...acc[item.ruleId],
                    surgeAmount: acc[item.ruleId].surgeAmount + item.surgeAmount,
                    vssSurgeAmount: acc[item.ruleId].vssSurgeAmount + item.vssSurgeAmount,
                };
            } else {
                acc[item.ruleId] = { ...item };
            }
            return acc;
        }, {})
    );

    const totalSurge = mergedItems.reduce((sum, item) => sum + item.surgeAmount, 0);
    const isPositive = totalSurge >= 0;

    return (
        <div>
            {/* Show bot message with price surge alert text */}
            <ChatMessage message={{
                ...message,
                message: 'Surcharge Alert'
            }} />

            {/* Price surge card */}
            <div className="my-3">
                <div className="bg-primary-50 rounded-lg p-5 shadow-sm max-w-2xl">
                    {/* Description */}
                    <p className="text-sm text-gray-700 mb-4">
                        Based on your selection, the following charges will be {isPositive ? 'added' : 'deducted'}:
                    </p>

                    {/* Surge items list */}
                    <div className="space-y-3 mb-5">
                        {mergedItems.map((item, index) => (
                            <div key={index} className="bg-primary-500/10 rounded-lg p-2">
                                <div className="flex items-start justify-between gap-4">
                                    <div className="flex-1">
                                        <div className="text-sm font-medium text-gray-900">
                                            {item.ruleName}
                                        </div>
                                        {item.description && (
                                            <div className="text-xs text-gray-500 italic mt-1">
                                                {item.description}
                                            </div>
                                        )}
                                    </div>
                                    <div className={`text-base font-bold whitespace-nowrap text-primary-500`}>
                                        {item.surgeAmount >= 0 ? '+' : '-'}{formatPrice(Math.abs(item.surgeAmount))}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                    {/* Total */}
                    <div className="flex items-center justify-between">
                        <span className="text-base font-semibold text-gray-900">
                            Total {isPositive ? 'Additional' : 'Discount'}:
                        </span>
                        <span className={`text-base font-bold text-primary-500`}>
                            {formatPrice(Math.abs(totalSurge))}
                        </span>
                    </div>
                </div>
            </div>
        </div>
    );
};
